import cx from 'classnames';
import PropTypes from 'prop-types';
import formatPrice from '@bwoty-web/price-formatter';
import { useState } from 'react';
import { UI_CHECKMARK } from '@bwoty-web/ui-kit/icons';
import { useGlobals, useIsGlobetrotter } from '@bwoty-web/ui-kit/hooks';
import {
  Button,
  CloudinaryPicture,
  Modal,
  Rating,
  Tag,
} from '@bwoty-web/ui-kit';

import Title from '../../common/title';
import UspList from '../../common/uspList';
import ReadMorePushes from './readMorePushes';
import RouteContent from './routeContent';
import KeySellingPointList from '../../common/keySellingPointList';
import { logCruiseDescription } from '../../utils/log';

import './cruiseDescriptionView.scss';

const CruiseDescriptionView = ({ cruiseContent, labels }) => {
  const {
    cruiseShipId,
    itinerary = [],
    ksps,
    name,
    popUpPage,
    price,
    routeMapImage,
    shipContent,
    shippingCompanyContent,
    shipRating,
    usps = [],
  } = cruiseContent;

  const [showRoutePopUp, setShowRoutePopUp] = useState(false);
  const isGlobe = useIsGlobetrotter();
  const { siteId } = useGlobals();
  return (
    <>
      <div className="gw-cruise-description__view uikit-box">
        <div className="gw-cruise-description__top-container">
          <div className="gw-cruise-description__top-wrapper">
            <div className="gw-cruise-description__titles">
              {shipContent?.tag && !isGlobe && (
                <div className="gw-cruise-tags">
                  <Tag small>{shipContent?.tag}</Tag>
                </div>
              )}
              <Title>{name}</Title>
              <div className="gw-cruise-description__sub-title-and-rating">
                <a
                  className="gw-cruise-description__sub-title"
                  href={`${labels.readMoreUrls.ship}?shipId=${cruiseShipId}`}
                >
                  {shipContent?.name}
                </a>
                <Rating
                  className="gw-cruise-description__rating"
                  rating={shipRating * 2}
                  size="s"
                  responsive
                />
              </div>
              {ksps?.length > 0 && (
                <KeySellingPointList
                  checkmark={!isGlobe}
                  className="gw-cruise-description__ksps"
                  ksps={ksps}
                />
              )}
              <UspList
                aboutLabel={labels?.aboutCruise}
                iconPath={UI_CHECKMARK}
                usps={usps.map((item) => item.content)}
              />
            </div>
            <div className="gw-cruise-description__top-side-bar">
              {!!price && (
                <div className="gw-cruise-description__price-wrapper">
                  <span className="gw-cruise-description__price-label">
                    {labels?.priceFrom}
                  </span>
                  <span
                    className={cx('gw-cruise-description__price', {
                      'gw-cruise-description__price--red': ![1, 18].includes(
                        siteId,
                      ),
                    })}
                  >
                    {formatPrice(price, siteId)}
                  </span>
                </div>
              )}
              {!popUpPage && (
                <Button
                  variant="primary"
                  data-gtm-tag="Show Prices"
                  href="#tdm-search-app-root"
                  id="gtm-cruisepage-pricebutton"
                >
                  {labels?.matrixAnchorButton}
                </Button>
              )}
            </div>
          </div>
        </div>
        <div className="gw-cruise-description__bottom-wrapper">
          {routeMapImage && (
            <CloudinaryPicture
              img={routeMapImage}
              sizes={routeMapImage.sizes}
              alt={routeMapImage.alt}
              className="gw-cruise-description__route-image"
              retina
            />
          )}
          {!isGlobe && (
            <Button
              onClick={() => {
                logCruiseDescription.clickOnCruiseRoute();
                setShowRoutePopUp(true);
              }}
              className="gw-cruise-description__route-popup-button"
            >
              {labels?.routePopupButtonText}
            </Button>
          )}
        </div>
        <Modal
          heading={labels?.routePopupHeader}
          isOpen={showRoutePopUp}
          onClose={() => setShowRoutePopUp(false)}
        >
          <RouteContent
            atSeaText={shipContent?.atSeaText}
            itinerary={itinerary}
            labels={labels}
          />
        </Modal>
      </div>
      <ReadMorePushes
        cruiseCompanyCode={shippingCompanyContent?.code}
        cruiseShipId={cruiseShipId}
        cruiseCompanyId={shipContent?.shippingCompanyId}
        routePopupOnClick={
          isGlobe &&
          (() => {
            logCruiseDescription.clickOnCruiseRoute();
            setShowRoutePopUp(true);
          })
        }
        labels={labels}
      />
    </>
  );
};

CruiseDescriptionView.propTypes = {
  cruiseContent: PropTypes.shape({
    name: PropTypes.string,
    usps: PropTypes.arrayOf(
      PropTypes.shape({
        content: PropTypes.string,
      }),
    ),
    cruiseShipId: PropTypes.number,
    shipRating: PropTypes.number,
    itinerary: PropTypes.arrayOf(PropTypes.shape({})),
    shipContent: PropTypes.shape({
      shippingCompanyId: PropTypes.number,
      name: PropTypes.string,
      url: PropTypes.string,
      tag: PropTypes.string,
      atSeaText: PropTypes.string,
    }).isRequired,
    shippingCompanyContent: PropTypes.shape({
      code: PropTypes.string.isRequired,
    }).isRequired,
    routeMapImage: PropTypes.shape({
      cloudinaryUriTemplate: PropTypes.string,
      alt: PropTypes.string,
      sizes: PropTypes.arrayOf(PropTypes.shape({})),
      fallback: PropTypes.string,
    }),
    price: PropTypes.string,
    popUpPage: PropTypes.bool,
    ksps: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  labels: PropTypes.shape({
    aboutCruise: PropTypes.string,
    dayText: PropTypes.string,
    matrixAnchorButton: PropTypes.string,
    priceFrom: PropTypes.string,
    readMoreUrls: PropTypes.shape({ ship: PropTypes.string }),
    routePopupButtonText: PropTypes.string,
    routePopupHeader: PropTypes.string,
  }).isRequired,
};

export default CruiseDescriptionView;
